import React, { useState, useEffect, useMemo, useRef } from "react";
import Nav from "./Navigation.js";
import {
  GRID_SIZE, KEY_ROW,
  buildGrid, isWon, getValidMoves, applyMove,
  solvePuzzle, formatMove, getPieceColor,
  generatePuzzle,
} from "./RushHourUtils.js";

const CELL = 60;
const GAP = 2;
const BOARD_PX = GRID_SIZE * CELL + (GRID_SIZE - 1) * GAP;

const DIFFICULTIES = [
  { key: 'medium', label: 'Medium', desc: '~8–12 moves' },
  { key: 'hard',   label: 'Hard',   desc: '~13–16 moves' },
  { key: 'expert', label: 'Expert', desc: '~17+ moves', wip: true },
];

function RushBoard({ pieces, dragging, hintPieceId, onPieceMouseDown, onPieceTouchStart }) {
  return (
    <div style={{ position: 'relative', width: BOARD_PX, height: BOARD_PX, userSelect: 'none' }}>
      {/* Background cells — decorative only */}
      {Array.from({ length: GRID_SIZE }, (_, r) =>
        Array.from({ length: GRID_SIZE }, (_, c) => (
          <div
            key={`${r}-${c}`}
            style={{
              position: 'absolute',
              top: r * (CELL + GAP),
              left: c * (CELL + GAP),
              width: CELL,
              height: CELL,
              backgroundColor: r === KEY_ROW ? '#1e3a5f' : '#374151',
              borderRadius: 3,
            }}
          />
        ))
      )}

      {/* Exit arrow */}
      <div style={{
        position: 'absolute',
        top: KEY_ROW * (CELL + GAP) + CELL / 2 - 10,
        left: BOARD_PX + 8,
        color: '#22c55e',
        fontSize: 22,
        fontWeight: 'bold',
        pointerEvents: 'none',
      }}>→</div>

      {pieces.map(piece => {
        const isDragging = dragging && piece.id === dragging.pieceId;
        const offsetPx = isDragging ? dragging.currentPx : 0;

        const top = piece.row * (CELL + GAP)
          + (piece.orientation === 'v' ? offsetPx : 0);
        const left = piece.col * (CELL + GAP)
          + (piece.orientation === 'h' ? offsetPx : 0);

        const w = piece.orientation === 'h'
          ? piece.length * CELL + (piece.length - 1) * GAP : CELL;
        const h = piece.orientation === 'v'
          ? piece.length * CELL + (piece.length - 1) * GAP : CELL;

        const isHint = piece.id === hintPieceId;

        return (
          <div
            key={piece.id}
            onMouseDown={piece.locked ? undefined : (e) => onPieceMouseDown(e, piece)}
            onTouchStart={piece.locked ? undefined : (e) => onPieceTouchStart(e, piece)}
            style={{
              position: 'absolute',
              top,
              left,
              width: w,
              height: h,
              backgroundColor: getPieceColor(piece),
              borderRadius: 6,
              border: isDragging
                ? '3px solid white'
                : isHint
                ? '3px solid #fbbf24'
                : '2px solid rgba(255,255,255,0.15)',
              zIndex: isDragging ? 10 : 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: piece.locked ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
              touchAction: 'none',
              transition: isDragging ? 'none' : 'border 0.1s',
              boxShadow: isDragging ? '0 4px 20px rgba(0,0,0,0.5)' : 'none',
            }}
          >
            {piece.isKey && (
              <span style={{ color: 'white', fontWeight: 'bold', fontSize: 11, letterSpacing: 1, pointerEvents: 'none' }}>KEY</span>
            )}
            {!piece.isKey && !piece.locked && (
              <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 'bold', fontSize: 13, pointerEvents: 'none' }}>{piece.id}</span>
            )}
            {piece.locked && <span style={{ fontSize: 16, pointerEvents: 'none' }}>🔒</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function RushHourGame() {
  const [difficulty, setDifficulty] = useState('hard');
  const [pieces, setPieces] = useState([]);
  const [initialPieces, setInitialPieces] = useState([]);
  const [moveCount, setMoveCount] = useState(0);
  const [won, setWon] = useState(false);
  const [hintPieceId, setHintPieceId] = useState(null);
  const [hintLabel, setHintLabel] = useState(null);
  const [solution, setSolution] = useState(null);
  const [solStep, setSolStep] = useState(0);
  const [showSol, setShowSol] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(null);

  // Keep latest pieces in a ref so drag handlers always see current state
  const piecesRef = useRef(pieces);
  useEffect(() => { piecesRef.current = pieces; }, [pieces]);

  const resetPlay = () => {
    setMoveCount(0);
    setWon(false);
    setHintPieceId(null);
    setHintLabel(null);
    setSolution(null);
    setSolStep(0);
    setShowSol(false);
    setDragging(null);
  };

  const loadGenerated = (p) => {
    setPieces(p.map(x => ({ ...x })));
    setInitialPieces(p.map(x => ({ ...x })));
    resetPlay();
  };

  const generateNew = (diff) => {
    setLoading(true);
    resetPlay();
    setTimeout(() => {
      const p = generatePuzzle(diff);
      if (p) loadGenerated(p);
      setLoading(false);
    }, 30);
  };

  useEffect(() => { generateNew('hard'); }, []);

  // Drag: window-level move + up listeners
  useEffect(() => {
    if (!dragging) return;

    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const raw = dragging.axis === 'h'
        ? clientX - dragging.startX
        : clientY - dragging.startY;
      const clamped = Math.max(dragging.minPx, Math.min(dragging.maxPx, raw));
      setDragging(prev => prev ? { ...prev, currentPx: clamped } : null);
    };

    const onUp = () => {
      const cur = dragging; // captured at effect time
      const cellDelta = Math.round(cur.currentPx / (CELL + GAP));
      if (cellDelta !== 0) {
        const move = {
          pieceId: cur.pieceId,
          dr: cur.axis === 'v' ? cellDelta : 0,
          dc: cur.axis === 'h' ? cellDelta : 0,
        };
        const currentPieces = piecesRef.current;
        const valid = getValidMoves(currentPieces);
        if (valid.some(m => m.pieceId === move.pieceId && m.dr === move.dr && m.dc === move.dc)) {
          const next = applyMove(currentPieces, move);
          setPieces(next);
          setMoveCount(n => n + 1);
          setHintPieceId(null);
          setHintLabel(null);
          if (isWon(next)) setWon(true);
        }
      }
      setDragging(null);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging]);

  const startDrag = (clientX, clientY, piece) => {
    if (won || showSol || loading || piece.locked) return;

    const valid = getValidMoves(piecesRef.current);
    const moves = valid.filter(m => m.pieceId === piece.id);
    if (moves.length === 0) return;

    const deltas = piece.orientation === 'h'
      ? moves.map(m => m.dc)
      : moves.map(m => m.dr);
    const minD = Math.min(...deltas, 0);
    const maxD = Math.max(...deltas, 0);

    setDragging({
      pieceId: piece.id,
      axis: piece.orientation,
      startX: clientX,
      startY: clientY,
      minPx: minD * (CELL + GAP),
      maxPx: maxD * (CELL + GAP),
      currentPx: 0,
    });
  };

  const handlePieceMouseDown = (e, piece) => {
    e.preventDefault();
    startDrag(e.clientX, e.clientY, piece);
  };

  const handlePieceTouchStart = (e, piece) => {
    e.preventDefault();
    startDrag(e.touches[0].clientX, e.touches[0].clientY, piece);
  };

  const handleHint = () => {
    const sol = solvePuzzle(pieces);
    if (!sol || sol.length === 0) {
      setHintLabel("Key can exit — drag it right!");
      return;
    }
    const move = sol[0];
    setHintPieceId(move.pieceId);
    setHintLabel(formatMove(move, pieces));
  };

  const handleShowSolution = () => {
    const sol = solvePuzzle(initialPieces);
    setSolution(sol);
    setSolStep(0);
    setShowSol(true);
    setHintPieceId(null);
    setHintLabel(null);
  };

  const solDisplayPieces = useMemo(() => {
    if (!showSol || !solution) return pieces;
    let p = initialPieces.map(x => ({ ...x }));
    for (let i = 0; i < solStep; i++) p = applyMove(p, solution[i]);
    return p;
  }, [showSol, solution, solStep, initialPieces, pieces]);

  const displayPieces = showSol ? solDisplayPieces : pieces;

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto my-8 text-center">
        <h1 className="text-3xl font-bold mb-1">Key Puzzle Practice</h1>
        <p className="text-gray-400 mb-5 text-sm">
          Drag blocks to get the <span style={{ color: '#dc2626' }}>red Key</span> to the right exit.
        </p>

        {/* Difficulty selector */}
        <div className="flex justify-center gap-2 mb-5">
          {DIFFICULTIES.map(d => (
            <button
              key={d.key}
              onClick={() => { setDifficulty(d.key); generateNew(d.key); }}
              disabled={loading}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50 ${
                difficulty === d.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {d.label}
              <span className="ml-1.5 text-xs opacity-60">{d.desc}</span>
              {d.wip && <span className="ml-1 text-xs">🚧</span>}
            </button>
          ))}
        </div>
        {difficulty === 'expert' && (
          <p className="text-yellow-500 text-xs mb-3 opacity-80">
            🚧 Expert randomizer is still being tuned — longer load times and occasional failures are expected. Thanks for your patience!
          </p>
        )}

        <div className="flex flex-col items-center gap-6 lg:flex-row lg:items-start lg:justify-center">
          {/* Board */}
          <div className="bg-gray-800 rounded-xl p-6 shadow-lg inline-block">
            <div className="flex justify-between items-center mb-3">
              <span className="text-gray-400 text-sm">
                Moves: <span className="text-white font-bold">{moveCount}</span>
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => { setPieces(initialPieces.map(x => ({ ...x }))); resetPlay(); }}
                  disabled={loading}
                  className="px-3 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 transition-colors"
                >
                  Reset
                </button>
                <button
                  onClick={() => generateNew(difficulty)}
                  disabled={loading}
                  className="px-3 py-1 text-xs rounded bg-blue-700 hover:bg-blue-600 disabled:opacity-40 transition-colors"
                >
                  {loading ? 'Generating…' : 'New Puzzle'}
                </button>
              </div>
            </div>

            {loading ? (
              <div
                style={{ width: BOARD_PX, height: BOARD_PX }}
                className="flex items-center justify-center bg-gray-700 rounded-lg"
              >
                <span className="text-gray-400 text-sm animate-pulse">Generating puzzle…</span>
              </div>
            ) : (
              <RushBoard
                pieces={displayPieces}
                dragging={showSol ? null : dragging}
                hintPieceId={showSol ? solution?.[solStep]?.pieceId : hintPieceId}
                onPieceMouseDown={showSol ? () => {} : handlePieceMouseDown}
                onPieceTouchStart={showSol ? () => {} : handlePieceTouchStart}
              />
            )}

            {won && !showSol && (
              <div className="mt-4 text-green-400 font-bold text-lg">
                Key escaped! Solved in {moveCount} moves.
              </div>
            )}
          </div>

          {/* Side panel */}
          <div className="flex flex-col gap-3 w-56">
            {!showSol && !loading && (
              <>
                <button
                  onClick={handleHint}
                  className="w-full py-2 rounded bg-yellow-600 hover:bg-yellow-500 text-white text-sm font-medium transition-colors"
                >
                  Hint
                </button>
                {hintLabel && (
                  <div className="bg-yellow-900 bg-opacity-40 border border-yellow-700 rounded p-2 text-yellow-300 text-sm text-left">
                    {hintLabel}
                  </div>
                )}
                <button
                  onClick={handleShowSolution}
                  className="w-full py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium transition-colors"
                >
                  Show Full Solution
                </button>
              </>
            )}

            {showSol && solution && (
              <div className="bg-gray-800 rounded-xl p-4 text-left">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-semibold">
                    Solution ({solution.length} moves)
                  </span>
                  <button
                    onClick={() => { setShowSol(false); setPieces(initialPieces.map(x => ({ ...x }))); resetPlay(); }}
                    className="text-xs text-gray-400 hover:text-white"
                  >
                    Close
                  </button>
                </div>
                <ol className="text-sm space-y-1 mb-4 max-h-72 overflow-y-auto pr-1">
                  {solution.map((move, i) => (
                    <li
                      key={i}
                      className={`px-2 py-1 rounded cursor-pointer ${
                        i === solStep ? 'bg-blue-700 text-white'
                        : i < solStep ? 'text-gray-500'
                        : 'text-gray-300 hover:bg-gray-700'
                      }`}
                      onClick={() => setSolStep(i)}
                    >
                      {i + 1}. {formatMove(move, initialPieces)}
                    </li>
                  ))}
                  <li className={`px-2 py-1 rounded text-green-400 font-medium ${solStep === solution.length ? 'bg-green-900 bg-opacity-40' : ''}`}>
                    ✓ Key exits
                  </li>
                </ol>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSolStep(s => Math.max(0, s - 1))}
                    disabled={solStep === 0}
                    className="flex-1 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 transition-colors"
                  >
                    ← Prev
                  </button>
                  <button
                    onClick={() => setSolStep(s => Math.min(solution.length, s + 1))}
                    disabled={solStep === solution.length}
                    className="flex-1 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 transition-colors"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}

            <div className="bg-gray-800 rounded-xl p-3 text-xs text-gray-400 text-left mt-2">
              <p className="font-semibold text-gray-300 mb-1">How to play</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Click and drag a piece to slide it</li>
                <li>Pieces only move along their own axis</li>
                <li>🔒 Locked pieces cannot be moved</li>
                <li>Get the red Key to the right exit</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
